import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule, KeyValuePipe } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { trigger, transition, style, animate } from '@angular/animations';
import { HeaderComponent } from '../header/header.component';
import { FooterComponent } from '../footer/footer.component';
import { ImageUploadComponent } from '../image-upload/image-upload.component';
import {
  CategoriesService,
  Category,
  Subcategory,
} from '../services/categories.service';
import { ListingsService, VehicleFeatures } from '../services/listings.service';
import { AuthService, User } from '../services/auth.service';
import { UserService } from '../services/user.service';
import { ImageUploadService } from '../services/image-upload.service';
import { DescriptionGenerationService } from '../services/description-generation.service';
import { getFormConfig, FormField } from '../utils/form-fields-configs';
import { SubcategoryImagesService } from '../services/subcategory-images.service';
import {
  LocationAutocompleteComponent,
  LocationSelectedEvent,
} from '../location-autocomplete/location-autocomplete.component';
import { LocationResult } from '../services/location.service';
import { VehicleSelectorComponent } from '../vehicle-selector/vehicle-selector.component';
import { MotorcycleBrandSelectorComponent } from '../vehicle-selector/motorcycle-brand-selector.component';

interface PricingPlan {
  name: string;
  price: number;
  color: string;
  gradient: string;
  features: {
    listingDuration: number;
    imagesAllowed: number;
    descriptionLength: number;
    appearsHigherInSearch: boolean;
    listingInFeaturedSection: boolean;
    listingInSuggestedItems: boolean;
    emailNotificationToSubscribers: boolean;
    freeRelist: boolean;
  };
  popular: boolean;
  displayFeatures: {
    imagesAllowed: string;
    descriptionLength: string;
    appearsHigherInSearch: string;
    listingInFeaturedSection: string;
    listingInSuggestedItems: string;
    emailNotificationToSubscribers: string;
    freeRelist: string;
  };
}

interface DateFieldConfig {
  yearRange: {
    start: number;
    end: number;
  };
  maxDate: Date;
}

@Component({
  selector: 'app-sell',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    HeaderComponent,
    FooterComponent,
    ImageUploadComponent,
    LocationAutocompleteComponent,
    VehicleSelectorComponent,
    MotorcycleBrandSelectorComponent,
    KeyValuePipe,
  ],
  templateUrl: './sell.component.html',
  styleUrls: ['./sell.component.css'],
  animations: [
    trigger('fadeAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(50px)' }),
        animate('1200ms cubic-bezier(0.22, 1, 0.36, 1)', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ])
  ]
})
export class SellComponent implements OnInit, OnDestroy {
  @ViewChild(ImageUploadComponent) imageUploadComponent!: ImageUploadComponent;

  private dateFieldsConfig: { [fieldName: string]: DateFieldConfig } = {
    first_registration: {
      yearRange: { start: 1950, end: new Date().getFullYear() },
      maxDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    },
    registered_until: {
      yearRange: {
        start: new Date().getFullYear(),
        end: new Date().getFullYear() + 1,
      },
      maxDate: new Date(
        new Date().getFullYear() + 1,
        new Date().getMonth(),
        1
      ),
    },
    // TO ADD A NEW DATE FIELD, JUST ADD IT HERE:
    // your_new_field_name: {
    //   yearRange: { start: YYYY, end: YYYY },
    //   maxDate: new Date(YYYY, MM, 1),
    // },
  };

  currentStep = 1;
  totalSteps = 6;
  categories: Category[] = [];
  subcategories: Subcategory[] = [];
  selectedCategory: Category | null = null;
  selectedSubcategory: Subcategory | null = null;
  selectedPlan: PricingPlan | null = null;
  vehicleFeatures: VehicleFeatures = {};
  selectedFeatures: number[] = [];
  currentUser: User | null = null;
  listingForm: FormGroup;
  formFields: FormField[] = [];
  isLoading = false;
  loadingCategories = false;
  loadingSubcategories = false;
  loadingFeatures = false;
  isSubmitting = false;
  successMessage = '';
  errorMessage = '';
  showSuccessModal = false;
  showPendingApprovalModal = false;
  uploadedImageUrls: string[] = [];
  imageUploadProgress = { uploaded: 0, total: 0 };
  imageUploadErrors: string[] = [];
  isCheckingImages = false;
  showPlanUpgradeSuggestion = false;
  suggestedPlan: PricingPlan | null = null;
  freeRelistCredits: any = null;
  hasFreeRelistCredits = false;
  usingFreeRelist = false;
  originalPlans: PricingPlan[] = [];
  showPublishingModal = false;
  publishingProgress = 0;
  publishingStage = '';
  publishingStages = [
    { name: 'Validating data...', progress: 10 },
    { name: 'Processing images...', progress: 30 },
    { name: 'Submitting listing...', progress: 70 },
    { name: 'Sending for review...', progress: 90 },
    { name: 'Submitted!', progress: 100 },
  ];
  currentStageIndex = 0;
  dateFieldsData: {
    [key: string]: {
      month: string;
      year: string;
    };
  } = {};
  dateFieldYearOptions: { [fieldName: string]: number[] } = {};
  monthOptions = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];
  isGeneratingDescription = false;
  descriptionGenerationError = '';
  generatedDescriptionMetadata: any = null;
  private isUpdatingPowerFields = false;
  private resizeHandler = this.handleViewportChange.bind(this);

  selectedLocationName: string = '';

  get isVehicleCategory(): boolean {
    return this.selectedCategory?.slug === 'vehicles';
  }

  get actualTotalSteps(): number {
    return this.isVehicleCategory ? 6 : 5;
  }

  plans: PricingPlan[] = [
    {
      name: 'Free',
      price: 0,
      color: '#6c757d',
      gradient: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
      features: {
        listingDuration: 7,
        imagesAllowed: 7,
        descriptionLength: 300,
        appearsHigherInSearch: false,
        listingInFeaturedSection: false,
        listingInSuggestedItems: false,
        emailNotificationToSubscribers: false,
        freeRelist: false,
      },
      displayFeatures: {
        imagesAllowed: '7 Images',
        descriptionLength: '300 Characters',
        appearsHigherInSearch: 'No',
        listingInFeaturedSection: 'No',
        listingInSuggestedItems: 'No',
        emailNotificationToSubscribers: 'No',
        freeRelist: 'No',
      },
      popular: false,
    },
    {
      name: 'Starter',
      price: 1.99,
      color: '#3498db',
      gradient: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
      features: {
        listingDuration: 14,
        imagesAllowed: 14,
        descriptionLength: 400,
        appearsHigherInSearch: true,
        listingInFeaturedSection: false,
        listingInSuggestedItems: false,
        emailNotificationToSubscribers: false,
        freeRelist: false,
      },
      displayFeatures: {
        imagesAllowed: '14 Images',
        descriptionLength: '400 Characters',
        appearsHigherInSearch: 'Yes',
        listingInFeaturedSection: 'No',
        listingInSuggestedItems: 'No',
        emailNotificationToSubscribers: 'No',
        freeRelist: 'No',
      },
      popular: false,
    },
    {
      name: 'Pro',
      price: 2.99,
      color: '#9b59b6',
      gradient: 'linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)',
      features: {
        listingDuration: 21,
        imagesAllowed: 21,
        descriptionLength: 500,
        appearsHigherInSearch: true,
        listingInFeaturedSection: true,
        listingInSuggestedItems: false,
        emailNotificationToSubscribers: true,
        freeRelist: false,
      },
      displayFeatures: {
        imagesAllowed: '21 Images',
        descriptionLength: '500 Characters',
        appearsHigherInSearch: 'Yes',
        listingInFeaturedSection: 'Yes',
        listingInSuggestedItems: 'No',
        emailNotificationToSubscribers: 'No',
        freeRelist: 'No',
      },
      popular: true,
    },
    {
      name: 'Elite',
      price: 3.99,
      color: '#e74c3c',
      gradient: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
      features: {
        listingDuration: 42,
        imagesAllowed: 42,
        descriptionLength: 5000,
        appearsHigherInSearch: true,
        listingInFeaturedSection: true,
        listingInSuggestedItems: true,
        emailNotificationToSubscribers: true,
        freeRelist: true,
      },
      displayFeatures: {
        imagesAllowed: '42 Images',
        descriptionLength: '5000 Characters',
        appearsHigherInSearch: 'Yes',
        listingInFeaturedSection: 'Yes',
        listingInSuggestedItems: 'Yes',
        emailNotificationToSubscribers: 'Yes',
        freeRelist: 'Yes (1 Free)',
      },
      popular: false,
    },
  ];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private categoriesService: CategoriesService,
    private listingsService: ListingsService,
    private authService: AuthService,
    private userService: UserService,
    private imageUploadService: ImageUploadService,
    private descriptionGenerationService: DescriptionGenerationService,
    private subcategoryImagesService: SubcategoryImagesService
  ) {
    this.listingForm = this.fb.group({});
  }

  ngOnInit(): void {
    this.loadCurrentUser();
    this.loadCategories();
    this.imageUploadService.generateNewSession();
    this.selectedPlan = this.plans[0];
    this.originalPlans = JSON.parse(JSON.stringify(this.plans));
    this.checkFreeRelistCredits();
    this.enableSmoothScrolling();
    this.initAllDateFieldYearOptions();
  }

  private initAllDateFieldYearOptions(): void {
    Object.keys(this.dateFieldsConfig).forEach((fieldName) => {
      this.dateFieldYearOptions[fieldName] =
        this.generateYearOptions(fieldName);
    });
  }

  private generateYearOptions(fieldName: string): number[] {
    const config = this.dateFieldsConfig[fieldName];
    if (!config) return [];

    const years: number[] = [];
    for (let year = config.yearRange.end; year >= config.yearRange.start; year--) {
      years.push(year);
    }
    return years;
  }

  getYearOptionsForField(fieldName: string): number[] {
    return this.dateFieldYearOptions[fieldName] || [];
  }

  isMonthDisabled(monthValue: string, fieldName: string): boolean {
    const data = this.dateFieldsData[fieldName];
    const config = this.dateFieldsConfig[fieldName];

    if (!data || !data.year || !config) return false;

    const selectedYear = parseInt(data.year, 10);
    const selectedMonth = parseInt(monthValue, 10);
    const maxDate = config.maxDate;

    const selectedDate = new Date(selectedYear, selectedMonth - 1, 1);

    return selectedDate > maxDate;
  }

  onDateMonthChange(event: Event, fieldName: string): void {
    const select = event.target as HTMLSelectElement;

    if (!this.dateFieldsData[fieldName]) {
      this.dateFieldsData[fieldName] = { month: '', year: '' };
    }

    this.dateFieldsData[fieldName].month = select.value;
    this.updateDateValue(fieldName);
  }

  onDateYearChange(event: Event, fieldName: string): void {
    const select = event.target as HTMLSelectElement;

    if (!this.dateFieldsData[fieldName]) {
      this.dateFieldsData[fieldName] = { month: '', year: '' };
    }

    this.dateFieldsData[fieldName].year = select.value;

    const month = this.dateFieldsData[fieldName].month;
    if (month && this.isMonthDisabled(month, fieldName)) {
      this.dateFieldsData[fieldName].month = '';
      const monthSelect = document.getElementById(
        fieldName + '_month'
      ) as HTMLSelectElement;
      if (monthSelect) monthSelect.value = '';
    }

    this.updateDateValue(fieldName);
  }

  private updateDateValue(fieldName: string): void {
    const data = this.dateFieldsData[fieldName];
    if (!data) return;

    const { month, year } = data;

    if (month && year) {
      const paddedMonth = month.padStart(2, '0');
      const dateString = `${paddedMonth}/${year}`;
      this.listingForm.get(fieldName)?.setValue(dateString);
      this.listingForm.get(fieldName)?.markAsTouched();
    } else {
      this.listingForm.get(fieldName)?.setValue('');
    }
  }

  private enableSmoothScrolling(): void {
    document.body.style.overflowX = 'hidden';
    document.body.style.overflowY = 'auto';
    document.documentElement.style.overflowX = 'hidden';
    document.documentElement.style.overflowY = 'auto';
    document.documentElement.style.scrollBehavior = 'smooth';
    window.addEventListener('resize', this.resizeHandler);
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.resizeHandler);
  }

  private handleViewportChange(): void {
    setTimeout(() => {
      document.body.style.overflowY = 'auto';
      document.documentElement.style.overflowY = 'auto';
    }, 100);
  }

  private scrollToTop(): void {
    this.performScrollToTop();

    setTimeout(() => {
      this.performScrollToTop();
    }, 50);

    setTimeout(() => {
      this.performScrollToTop();
    }, 200);
  }

  private performScrollToTop(): void {
    try {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;

      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    } catch (error) {
      try {
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      } catch (fallbackError) {
        requestAnimationFrame(() => {
          window.scrollTo(0, 0);
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
        });
      }
    }
  }

  getSubcategoryImage(slug: string) {
    return this.subcategoryImagesService.getSubcategoryImage(slug);
  }

  onImageError(event: Event): void {
    const target = event.target as HTMLImageElement;
    if (target) {
      target.src = 'assets/images/listing-placeholder/no-image-placeholder.png';
    }
  }

  getGenerateButtonTooltip(): string {
    if (this.isGeneratingDescription) {
      return 'AI is analyzing your images and title to create a perfect description...';
    }

    if (this.generatedDescriptionMetadata && !this.descriptionGenerationError) {
      return `✨ Description generated successfully! Click to regenerate with fresh AI analysis. Current: ${this.generatedDescriptionMetadata.wordCount} words, ${this.generatedDescriptionMetadata.characterCount} characters.`;
    }

    if (this.descriptionGenerationError) {
      return `❌ Generation failed: ${this.descriptionGenerationError}. Click to try again.`;
    }

    if (!this.canGenerateDescription()) {
      const issues = this.getGenerationIssues();
      return `Cannot generate description: ${issues.join(', ')}`;
    }

    const imageCount = this.uploadedImageUrls.length;
    const hasImages = imageCount > 0;

    if (hasImages) {
      return `🤖 Generate AI description from your title and ${imageCount} image${
        imageCount !== 1 ? 's' : ''
      }. Our AI will analyze colors, condition, and features visible in your photos.`;
    } else {
      return `🤖 Generate AI description from your title. Add images for even better results - our AI can analyze colors, condition, and features!`;
    }
  }

  getGenerationIssues(): string[] {
    const issues: string[] = [];

    if (!this.selectedCategory) {
      issues.push('select a category');
    }

    if (!this.selectedSubcategory) {
      issues.push('select a subcategory');
    }

    const title = this.listingForm.get('title')?.value;
    if (!title || title.trim().length < 3) {
      issues.push('add a title (minimum 3 characters)');
    }

    if (!this.descriptionGenerationService.isServiceAvailable()) {
      issues.push('AI service is currently unavailable');
    }

    return issues;
  }

  canGenerateDescription(): boolean {
    if (this.isGeneratingDescription) {
      return false;
    }

    if (!this.selectedCategory || !this.selectedSubcategory) {
      return false;
    }

    const title = this.listingForm.get('title')?.value;
    if (!title || title.trim().length < 3) {
      return false;
    }

    if (!this.descriptionGenerationService.isServiceAvailable()) {
      return false;
    }

    return true;
  }

  async generateDescription(): Promise<void> {
    if (!this.canGenerateDescription()) {
      const issues = this.getGenerationIssues();
      this.errorMessage = `Cannot generate description: ${issues.join(', ')}`;
      return;
    }

    this.isGeneratingDescription = true;
    this.descriptionGenerationError = '';
    this.generatedDescriptionMetadata = null;
    this.clearMessages();

    try {
      const title = this.listingForm.get('title')?.value?.trim();
      const currentPlan = this.selectedPlan?.name || 'Free';
      const imageCount = this.uploadedImageUrls.length;

      if (imageCount > 0) {
        this.successMessage = `🔍 AI is analyzing your ${imageCount} image${
          imageCount !== 1 ? 's' : ''
        } and title...`;
      } else {
        this.successMessage = `🤖 AI is creating a description from your title and photos...`;
      }

      const request = {
        title,
        images: this.uploadedImageUrls,
        plan_name: currentPlan,
      };

      const validation =
        this.descriptionGenerationService.validateGenerationRequest(request);
      if (!validation.valid) {
        throw new Error(validation.errors.join(', '));
      }

      const response = await this.descriptionGenerationService
        .generateDescription(
          this.selectedCategory!.slug,
          this.selectedSubcategory!.slug,
          request
        )
        .toPromise();

      if (response && response.success) {
        this.listingForm.patchValue({
          description: response.description,
        });

        this.generatedDescriptionMetadata = response.metadata;

        const analysisType = imageCount > 0 ? 'image analysis and' : '';
        this.successMessage = `✨ Perfect! AI generated a ${response.metadata.wordCount}-word description using ${analysisType} title context. Review and edit as needed!`;

        const descriptionControl = this.listingForm.get('description');
        if (descriptionControl) {
          descriptionControl.markAsTouched();
          descriptionControl.updateValueAndValidity();
        }

        setTimeout(() => {
          if (this.successMessage.includes('Perfect! AI generated')) {
            this.clearMessages();
          }
        }, 6000);
      } else {
        throw new Error('No description received from AI service');
      }
    } catch (error: any) {
      console.error('❌ Description generation error:', error);
      this.descriptionGenerationError = this.getHumanReadableError(
        error.message
      );
      this.errorMessage = `Failed to generate description: ${this.descriptionGenerationError}`;
    } finally {
      this.isGeneratingDescription = false;
    }
  }

  onLocationSelected(location: LocationResult): void {
    this.selectedLocationName = location.formatted;
    this.successMessage = `📍 Location set to: ${location.formatted}`;
    setTimeout(() => this.clearMessages(), 3000);
  }

  onLocationSelectedWithCoords(locationEvent: LocationSelectedEvent): void {
    this.selectedLocationName = locationEvent.formatted;
    this.successMessage = `📍 Location set to: ${locationEvent.formatted}`;
    setTimeout(() => this.clearMessages(), 3000);
  }

  getHumanReadableError(errorMessage: string): string {
    if (errorMessage.includes('Rate limit exceeded')) {
      return 'Too many requests. Please wait a moment and try again.';
    }

    if (errorMessage.includes('Service temporarily unavailable')) {
      return 'AI service is temporarily down. Please try again in a few minutes.';
    }

    if (errorMessage.includes('Invalid request parameters')) {
      return 'Invalid input. Please check your title and images.';
    }

    if (errorMessage.includes('timeout')) {
      return 'Request timed out. Please try again.';
    }

    if (errorMessage.includes('Title must be at least 3 characters')) {
      return 'Please add a longer title (minimum 3 characters).';
    }

    return errorMessage || 'Unknown error occurred';
  }

  clearGeneratedDescription(): void {
    const currentDescription = this.listingForm.get('description')?.value || '';

    if (currentDescription.length > 100) {
      const confirmClear = confirm(
        `Are you sure you want to clear this description?\n\n` +
          `Current description: ${currentDescription.length} characters\n` +
          `This action cannot be undone.`
      );

      if (!confirmClear) {
        return;
      }
    }

    this.listingForm.patchValue({
      description: '',
    });

    this.generatedDescriptionMetadata = null;
    this.descriptionGenerationError = '';
    this.clearMessages();

    this.successMessage = '🗑️ Description cleared. Ready for a fresh start!';
    setTimeout(() => this.clearMessages(), 3000);
  }

  onImagesChange(urls: string[]): void {
    this.uploadedImageUrls = urls;
    this.validateImageCount();

    const imageCount = this.getImageCount();
    if (
      imageCount <= 42 &&
      (!this.selectedPlan ||
        imageCount <= this.selectedPlan.features.imagesAllowed)
    ) {
      if (this.errorMessage && this.errorMessage.includes('images')) {
        this.clearMessages();
      }
    }
  }

  onImageUploadComplete(result: { urls: string[]; errors: string[] }): void {
    this.uploadedImageUrls = result.urls;
    this.imageUploadErrors = result.errors;

    if (result.errors.length > 0) {
      this.errorMessage = `Image upload issues: ${result.errors.join(', ')}`;
    } else if (result.urls.length > 0) {
      const imageCount = result.urls.length;
      this.successMessage = `${imageCount} image${
        imageCount !== 1 ? 's' : ''
      } uploaded successfully!`;

      if (imageCount > 7) {
        const suggestion = this.getImagePlanSuggestion();
        if (suggestion && suggestion.name !== 'Free') {
          this.successMessage += ` Note: ${imageCount} images requires ${suggestion.name} plan or higher.`;
        }
      }

      setTimeout(() => this.clearMessages(), 5000);
    }

    this.validateImageCount();
  }

  onImageUploadProgress(progress: { uploaded: number; total: number }): void {
    this.imageUploadProgress = progress;
  }

  getImageUploadProgress(): number {
    if (this.imageUploadProgress.total === 0) return 0;
    return (
      (this.imageUploadProgress.uploaded / this.imageUploadProgress.total) * 100
    );
  }

  getImageCount(): number {
    return this.uploadedImageUrls.length;
  }

  getImagePlanValidationError(): string | null {
    const imageCount = this.getImageCount();

    if (
      this.currentStep >= (this.isVehicleCategory ? 5 : 4) &&
      this.selectedPlan
    ) {
      const maxImages = this.selectedPlan.features.imagesAllowed;
      if (imageCount > maxImages) {
        return `Your ${
          this.selectedPlan.name
        } plan allows maximum ${maxImages} images. Please remove ${
          imageCount - maxImages
        } image(s) or upgrade your plan.`;
      }
    }

    return null;
  }

  getImagePlanSuggestion(): PricingPlan | null {
    const imageCount = this.getImageCount();

    for (const plan of this.plans) {
      if (imageCount <= plan.features.imagesAllowed) {
        return plan;
      }
    }

    return null;
  }

  validateImagePlanLimits(): boolean {
    const imageCount = this.getImageCount();

    if (imageCount > 42) {
      this.errorMessage = 'Maximum 42 images allowed (Elite plan limit)';
      return false;
    }

    if (
      this.currentStep >= (this.isVehicleCategory ? 5 : 4) &&
      this.selectedPlan
    ) {
      const maxImages = this.selectedPlan.features.imagesAllowed;
      if (imageCount > maxImages) {
        return false;
      }
    }

    return true;
  }

  validateImageCount(): void {
    const imageCount = this.getImageCount();

    if (imageCount > 42) {
      this.errorMessage =
        'Maximum 42 images allowed. Please remove some images.';
      return;
    }

    if (
      this.currentStep >= (this.isVehicleCategory ? 5 : 4) &&
      this.selectedPlan
    ) {
      const maxImages = this.selectedPlan.features.imagesAllowed;
      if (imageCount > maxImages) {
        const suggestion = this.getImagePlanSuggestion();
        if (suggestion && suggestion.name !== this.selectedPlan.name) {
          this.errorMessage = `Your ${
            this.selectedPlan.name
          } plan allows maximum ${maxImages} images. Consider upgrading to ${
            suggestion.name
          } plan (${suggestion.features.imagesAllowed} images) or remove ${
            imageCount - maxImages
          } image(s).`;
        } else {
          this.errorMessage = `Your ${
            this.selectedPlan.name
          } plan allows maximum ${maxImages} images. Please remove ${
            imageCount - maxImages
          } image(s).`;
        }
      } else {
        if (this.errorMessage && this.errorMessage.includes('images allowed')) {
          this.clearMessages();
        }
      }
    }
  }

  canProceedFromDetails(): boolean {
    if (this.listingForm.invalid) {
      return false;
    }

    if (this.getImageCount() > 42) {
      return false;
    }

    if (this.imageUploadComponent) {
      const readyState = this.imageUploadComponent.isReadyForSubmission();
      if (!readyState.ready) {
        return false;
      }
    }

    return true;
  }

  loadCurrentUser(): void {
    this.authService.currentUser$.subscribe((user) => {
      this.currentUser = user;
    });

    this.authService.getCurrentUser().subscribe();
  }

  loadCategories(): void {
    this.loadingCategories = true;
    this.categoriesService.getCategories().subscribe({
      next: (categories) => {
        this.categories = categories;
        this.loadingCategories = false;
      },
      error: (error) => {
        this.errorMessage = 'Failed to load categories';
        this.loadingCategories = false;
        console.error('Error loading categories:', error);
      },
    });
  }

  selectCategory(category: Category): void {
    this.selectedCategory = category;
    this.selectedSubcategory = null;
    this.loadSubcategories(category.slug);
    this.nextStep();
  }

  loadSubcategories(categorySlug: string): void {
    this.loadingSubcategories = true;
    this.categoriesService.getSubcategories(categorySlug).subscribe({
      next: (subcategories) => {
        this.subcategories = subcategories;
        this.loadingSubcategories = false;
      },
      error: (error) => {
        this.errorMessage = 'Failed to load subcategories';
        this.loadingSubcategories = false;
        console.error('Error loading subcategories:', error);
      },
    });
  }

  selectSubcategory(subcategory: Subcategory): void {
    this.selectedSubcategory = subcategory;
    this.nextStep();
  }

  canContentFitInPlan(plan: PricingPlan): boolean {
    const description = this.listingForm.get('description')?.value || '';
    const imageCount = this.uploadedImageUrls.length;

    return (
      imageCount <= plan.features.imagesAllowed &&
      description.length <= plan.features.descriptionLength
    );
  }

  getDescriptionLength(): number {
    return (this.listingForm.get('description')?.value || '').length;
  }

  canProceedWithSelectedPlan(): boolean {
    if (!this.selectedPlan) return false;
    if (!this.canAffordPlan(this.selectedPlan)) return false;

    if (
      !this.canContentFitInPlan(this.selectedPlan) &&
      this.showPlanUpgradeSuggestion
    ) {
      return false;
    }

    return true;
  }

  validateContentAgainstPlan(): {
    valid: boolean;
    errors: string[];
    needsUpgrade: boolean;
    suggestedPlan?: PricingPlan;
  } {
    if (!this.selectedPlan) {
      return {
        valid: false,
        errors: ['No plan selected'],
        needsUpgrade: false,
      };
    }

    const errors: string[] = [];
    const description = this.listingForm.get('description')?.value || '';
    const imageCount = this.getImageCount();

    if (imageCount > this.selectedPlan.features.imagesAllowed) {
      errors.push(
        `Your content has ${imageCount} images, but ${this.selectedPlan.name} plan only allows ${this.selectedPlan.features.imagesAllowed} images`
      );
    }

    if (description.length > this.selectedPlan.features.descriptionLength) {
      errors.push(
        `Your description has ${description.length} characters, but ${this.selectedPlan.name} plan only allows ${this.selectedPlan.features.descriptionLength} characters`
      );
    }

    let suggestedPlan: PricingPlan | undefined;
    if (errors.length > 0) {
      for (const plan of this.plans) {
        if (
          imageCount <= plan.features.imagesAllowed &&
          description.length <= plan.features.descriptionLength
        ) {
          suggestedPlan = plan;
          break;
        }
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      needsUpgrade: errors.length > 0,
      suggestedPlan,
    };
  }

  showPlanValidationError(validation: {
    valid: boolean;
    errors: string[];
    needsUpgrade: boolean;
    suggestedPlan?: PricingPlan;
  }): void {
    let message = validation.errors.join('. ');

    if (validation.suggestedPlan) {
      message += ` We recommend upgrading to the ${validation.suggestedPlan.name} plan ($${validation.suggestedPlan.price}) which supports your content.`;
    } else {
      message += ' Please go back and reduce your content or contact support.';
    }

    this.errorMessage = message;

    this.showPlanUpgradeSuggestion = true;
    this.suggestedPlan = validation.suggestedPlan ?? null;
  }

  acceptSuggestedPlan(): void {
    if (this.suggestedPlan && this.canAffordPlan(this.suggestedPlan)) {
      this.selectPlan(this.suggestedPlan);
      this.showPlanUpgradeSuggestion = false;
      this.suggestedPlan = null;
      this.clearMessages();
      this.currentStep = this.actualTotalSteps;
      setTimeout(() => this.scrollToTop(), 10);
    }
  }

  goBackToReduceContent(): void {
    this.showPlanUpgradeSuggestion = false;
    this.suggestedPlan = null;
    this.clearMessages();
    this.currentStep = 3;
    setTimeout(() => this.scrollToTop(), 10);
  }

  dismissUpgradeSuggestion(): void {
    this.showPlanUpgradeSuggestion = false;
    this.suggestedPlan = null;
    this.clearMessages();
  }

  selectPlan(plan: PricingPlan): void {
    if (!this.usingFreeRelist && !this.canAffordPlan(plan)) {
      this.errorMessage = `Insufficient balance. You need $${(
        plan.price - (this.currentUser?.balance || 0)
      ).toFixed(2)} more.`;
      return;
    }

    this.selectedPlan = plan;
    this.showPlanUpgradeSuggestion = false;
    this.suggestedPlan = null;

    if (this.currentStep >= 4) {
      const imageCount = this.getImageCount();
      const description = this.listingForm.get('description')?.value || '';
      const warnings: string[] = [];

      if (imageCount > plan.features.imagesAllowed) {
        warnings.push(
          `${imageCount} images (plan allows ${plan.features.imagesAllowed})`
        );
      }

      if (description.length > plan.features.descriptionLength) {
        warnings.push(
          `${description.length} characters (plan allows ${plan.features.descriptionLength})`
        );
      }

      if (warnings.length > 0) {
        this.errorMessage = `Selected plan cannot accommodate your content: ${warnings.join(
          ', '
        )}. Please choose a higher plan or reduce your content.`;
      } else {
        this.clearMessages();
        if (this.usingFreeRelist) {
          this.successMessage = `✨ Using free relist - ${plan.name} plan for $0.00!`;
        }
      }
    }
  }

  checkFreeRelistCredits(): void {
    this.userService.checkFreeRelistCredits().subscribe({
      next: (credits) => {
        this.freeRelistCredits = credits;
        this.hasFreeRelistCredits = credits.has_free_relist_credits;

        if (this.hasFreeRelistCredits) {
          let successMsg = credits.message;
          if (credits.details && credits.details.length > 0) {
            const activeCount = credits.active_credits || 0;
            const expiredCount = credits.expired_credits || 0;

            if (activeCount > 0) {
              successMsg += ` You can use these credits for any new listing!`;
            }
          }

          this.successMessage = successMsg;

          setTimeout(() => {
            if (this.successMessage === successMsg) {
              this.clearMessages();
            }
          }, 5000);
        }
      },
      error: (error) => {
        console.error('❌ Error checking free relist credits:', error);
        this.hasFreeRelistCredits = false;
        this.errorMessage =
          'Could not check free relist credits. Please try again.';
      },
    });
  }

  toggleFreeRelist(): void {
    this.usingFreeRelist = !this.usingFreeRelist;

    if (this.usingFreeRelist) {
      if (
        !this.hasFreeRelistCredits ||
        !this.freeRelistCredits ||
        this.freeRelistCredits.available_credits === 0
      ) {
        this.errorMessage = 'No free relist credits available!';
        this.usingFreeRelist = false;
        return;
      }

      this.plans = this.plans.map((plan) => ({
        ...plan,
        price: 0,
      }));

      this.successMessage = `🎉 Free relist activated! All plans are now $0.00`;
    } else {
      this.plans = JSON.parse(JSON.stringify(this.originalPlans));
      this.clearMessages();
    }
  }

  canAffordPlan(plan: PricingPlan): boolean {
    if (this.usingFreeRelist) {
      return true;
    }
    return (this.currentUser?.balance || 0) >= plan.price;
  }

  getMaxImagesForCurrentPlan(): number {
    if (this.currentStep === 3) {
      return 42;
    }
    return this.selectedPlan?.features.imagesAllowed || 42;
  }

  getMaxDescriptionLengthForCurrentPlan(): number {
    if (this.currentStep === 3) {
      return 5000;
    }
    return this.selectedPlan?.features.descriptionLength || 5000;
  }

  updateDescriptionValidation(): void {
    if (!this.selectedPlan || !this.listingForm.get('description')) return;

    const descriptionControl = this.listingForm.get('description');
    const maxLength =
      this.currentStep === 3
        ? 5000
        : this.selectedPlan.features.descriptionLength;

    const newValidators = [
      Validators.required,
      Validators.minLength(10),
      Validators.maxLength(maxLength),
    ];

    descriptionControl?.setValidators(newValidators);
    descriptionControl?.updateValueAndValidity();
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.clearMessages();
      setTimeout(() => {
        this.scrollToTop();
      }, 10);
    }
  }

  private setupPowerFieldsConversion(): void {
    const powerKwControl = this.listingForm.get('power_kw');
    const powerHpControl = this.listingForm.get('power_hp');

    if (powerKwControl && powerHpControl) {
      powerKwControl.valueChanges.subscribe((value) => {
        if (!this.isUpdatingPowerFields && value !== null && value !== '') {
          this.isUpdatingPowerFields = true;
          const hp = this.convertKwToHp(parseFloat(value));
          if (!isNaN(hp)) {
            powerHpControl.setValue(Math.round(hp), { emitEvent: false });
            powerHpControl.markAsTouched();
            powerHpControl.updateValueAndValidity();
          }
          this.isUpdatingPowerFields = false;
        }
      });

      powerHpControl.valueChanges.subscribe((value) => {
        if (!this.isUpdatingPowerFields && value !== null && value !== '') {
          this.isUpdatingPowerFields = true;
          const kw = this.convertHpToKw(parseFloat(value));
          if (!isNaN(kw)) {
            powerKwControl.setValue(Math.round(kw), { emitEvent: false });
          }
          this.isUpdatingPowerFields = false;
        }
      });
    }
  }

  private convertKwToHp(kw: number): number {
    return kw * 1.35962162;
  }

  private convertHpToKw(hp: number): number {
    return hp * 0.735498757;
  }

  setupForm(): void {
    if (!this.selectedCategory || !this.selectedSubcategory) return;

    this.formFields = getFormConfig(
      this.selectedCategory.slug,
      this.selectedSubcategory.slug
    );

    if (this.formFields.length === 0) {
      this.errorMessage = `No form configuration found for ${this.selectedSubcategory.name}. This subcategory may not be supported yet.`;
      console.error(
        'No form fields found for subcategory:',
        this.selectedSubcategory.slug
      );
      return;
    }

    const formControls: { [key: string]: any } = {};

    this.formFields.forEach((field) => {
      let validators = field.validators || [];
      let defaultValue: any = '';

      if (field.type === 'checkbox') {
        defaultValue = false;
      } else if (field.type === 'number') {
        defaultValue = null;
      } else if (field.name === 'currency') {
        defaultValue = 'EUR';
      } else if (
        field.name === 'price' &&
        this.selectedSubcategory?.slug === 'giving-away'
      ) {
        defaultValue = 0;
      } else if (field.type === 'vehicle-selector') {
        defaultValue = { brand: '', model: '' };
      }

      if (field.name === 'description') {
        validators = [
          Validators.required,
          Validators.minLength(10),
          Validators.maxLength(5000),
        ];
      }

      formControls[field.name] = [defaultValue, validators];
    });

    if (this.selectedCategory.slug === 'vehicles') {
      formControls['features'] = [[]];
    }

    this.listingForm = this.fb.group(formControls);

    this.formFields.forEach((field) => {
      if (field.disabled) {
        this.listingForm.get(field.name)?.disable();
      }
    });
    this.setupPowerFieldsConversion();
  }

  nextStep(): void {
    switch (this.currentStep) {
      case 1:
        if (this.selectedCategory) {
          this.currentStep = 2;
          this.clearMessages();
          setTimeout(() => this.scrollToTop(), 10);
        }
        break;

      case 2:
        if (this.selectedSubcategory) {
          this.setupForm();
          this.currentStep = 3;
          this.clearMessages();
          setTimeout(() => this.scrollToTop(), 10);
        }
        break;

      case 3:
        if (this.formFields.length === 0) {
          this.errorMessage =
            'Cannot proceed: No form fields available for this subcategory.';
          return;
        }

        if (!this.canProceedFromDetails()) {
          Object.keys(this.listingForm.controls).forEach((key) => {
            const control = this.listingForm.get(key);
            control?.markAsTouched();
          });
          return;
        }

        if (this.isVehicleCategory) {
          this.loadVehicleFeaturesIfNeeded();
          this.currentStep = 4;
          this.clearMessages();
          setTimeout(() => this.scrollToTop(), 10);
        } else {
          this.currentStep = 4;
          this.clearMessages();
          setTimeout(() => this.scrollToTop(), 10);
        }
        break;

      case 4:
        if (this.isVehicleCategory) {
          this.currentStep = 5;
          this.clearMessages();
          setTimeout(() => this.scrollToTop(), 10);
        } else {
          if (!this.selectedPlan) {
            this.errorMessage = 'Please select a plan to continue.';
            return;
          }

          if (!this.canAffordPlan(this.selectedPlan)) {
            this.errorMessage = 'Insufficient balance for selected plan.';
            return;
          }

          if (!this.canContentFitInPlan(this.selectedPlan)) {
            const validation = this.validateContentAgainstPlan();
            this.showPlanValidationError(validation);
            return;
          }

          this.currentStep = this.actualTotalSteps;
          this.clearMessages();
          setTimeout(() => this.scrollToTop(), 10);
        }
        break;

      case 5:
        if (this.isVehicleCategory) {
          if (!this.selectedPlan) {
            this.errorMessage = 'Please select a plan to continue.';
            return;
          }

          if (!this.canAffordPlan(this.selectedPlan)) {
            this.errorMessage = 'Insufficient balance for selected plan.';
            return;
          }

          if (!this.canContentFitInPlan(this.selectedPlan)) {
            const validation = this.validateContentAgainstPlan();
            this.showPlanValidationError(validation);
            return;
          }

          this.currentStep = this.actualTotalSteps;
          this.clearMessages();
          setTimeout(() => this.scrollToTop(), 10);
        }
        break;
    }
  }

  showValidationErrors(): void {
    const errors: string[] = [];

    if (this.selectedPlan) {
      const description = this.listingForm.get('description')?.value || '';
      if (description.length > this.selectedPlan.features.descriptionLength) {
        errors.push(
          `Description exceeds ${this.selectedPlan.name} plan limit of ${this.selectedPlan.features.descriptionLength} characters`
        );
      }

      if (
        this.uploadedImageUrls.length > this.selectedPlan.features.imagesAllowed
      ) {
        errors.push(
          `Too many images for ${this.selectedPlan.name} plan (max: ${this.selectedPlan.features.imagesAllowed})`
        );
      }
    }

    if (errors.length > 0) {
      this.errorMessage = errors.join('. ');
    }
  }

  loadVehicleFeaturesIfNeeded(): void {
    if (
      this.selectedCategory?.slug === 'vehicles' &&
      this.selectedSubcategory
    ) {
      this.loadingFeatures = true;
      this.listingsService
        .getVehicleFeatures(this.selectedSubcategory.slug)
        .subscribe({
          next: (features) => {
            this.vehicleFeatures = features;
            this.loadingFeatures = false;
          },
          error: (error) => {
            console.error('Error loading vehicle features:', error);
            this.loadingFeatures = false;
          },
        });
    }
  }

   onFeatureChange(featureId: number, event: any): void {
    if (event.target.checked) {
      this.selectedFeatures.push(featureId);
    } else {
      this.selectedFeatures = this.selectedFeatures.filter(
        (id) => id !== featureId
      );
    }

    this.listingForm.patchValue({ features: this.selectedFeatures });
  }

  selectAllFeatures(): void {
    const allFeatureIds: number[] = [];
    Object.values(this.vehicleFeatures).forEach((features) => {
      features.forEach((feature) => allFeatureIds.push(feature.id));
    });
    this.selectedFeatures = [...allFeatureIds];
    this.listingForm.patchValue({ features: this.selectedFeatures });
  }

  clearAllFeatures(): void {
    this.selectedFeatures = [];
    this.listingForm.patchValue({ features: this.selectedFeatures });
  }

  formatCategoryName(categoryKey: string): string {
    return categoryKey
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  getSelectedFeaturesInCategory(features: any[]): any[] {
    return features.filter((feature) =>
      this.selectedFeatures.includes(feature.id)
    );
  }

  getFieldValue(fieldName: string): any {
    return this.listingForm.get(fieldName)?.value;
  }

  formatFieldValue(field: FormField, value: any): string {
    if (value === null || value === undefined || value === '') {
      return 'Not specified';
    }

    if (field.type === 'vehicle-selector') {
      if (typeof value === 'object' && value.brand && value.model) {
        return `${value.brand} ${value.model}`;
      } else if (typeof value === 'string') {
        return value;
      }
      return 'Not specified';
    }

    if (field.type === 'checkbox') {
      return value ? 'Yes' : 'No';
    }

    if (field.type === 'select' && field.options) {
      const option = field.options.find((opt) => opt.value === value);
      return option ? option.label : value;
    }

    if (
      field.type === 'number' &&
      (field.name.includes('price') ||
        field.name.includes('rent') ||
        field.name.includes('budget'))
    ) {
      const currency = this.listingForm.get('currency')?.value || 'EUR';
      const symbol = currency === 'EUR' ? '€' : currency === 'USD' ? '$' : '£';
      return `${symbol}${Number(value).toFixed(2)}`;
    }

    if (field.type === 'date') {
      return new Date(value).toLocaleDateString();
    }

    return value.toString();
  }

  formatBalance(balance?: number | string | null): string {
    if (balance === undefined || balance === null) {
      return '$0.00';
    }

    const numBalance =
      typeof balance === 'string' ? parseFloat(balance) : balance;
    return `$${numBalance.toFixed(2)}`;
  }

  getEffectivePlanPrice(plan: PricingPlan): number {
    const effectivePrice = this.usingFreeRelist ? 0 : plan.price;
    return effectivePrice;
  }

  formatEffectivePlanPrice(plan: PricingPlan): string {
    const effectivePrice = this.getEffectivePlanPrice(plan);
    return effectivePrice === 0 ? 'Free' : `$${effectivePrice.toFixed(2)}`;
  }

  calculateExpirationDate(): Date | null {
    if (!this.selectedPlan) return null;

    const currentDate = new Date();
    const expirationDate = new Date(currentDate);
    expirationDate.setDate(
      currentDate.getDate() + this.selectedPlan.features.listingDuration
    );

    return expirationDate;
  }

  formatExpirationDate(): string {
    const expDate = this.calculateExpirationDate();
    return expDate ? expDate.toLocaleDateString() : 'Not calculated';
  }

  async submitListing(): Promise<void> {
    if (
      !this.selectedCategory ||
      !this.selectedSubcategory ||
      !this.selectedPlan ||
      this.listingForm.invalid
    ) {
      this.errorMessage = 'Please complete all required fields.';
      return;
    }

    const validation = this.validateContentAgainstPlan();
    if (!validation.valid) {
      this.errorMessage = `Cannot submit: ${validation.errors.join('. ')}`;
      return;
    }

    const effectivePrice = this.getEffectivePlanPrice(this.selectedPlan);

    if (
      !this.usingFreeRelist &&
      !(this.currentUser && this.canAffordPlan(this.selectedPlan))
    ) {
      this.errorMessage = 'Insufficient balance for selected plan.';
      return;
    }

    this.startPublishingProcess();

    try {
      const formData = await this.prepareFormData(effectivePrice);
      await this.createListingWithProgress(formData);
    } catch (error) {
      this.hidePublishingModal();
      console.error('❌ Error creating listing:', error);
      this.errorMessage = (error as any).message || 'Failed to create listing';
    }
  }

  private startPublishingProcess(): void {
    this.showPublishingModal = true;
    this.publishingProgress = 0;
    this.currentStageIndex = 0;
    this.publishingStage = this.publishingStages[0].name;
    this.isSubmitting = true;
    this.clearMessages();
    document.body.style.overflow = 'hidden';
  }

  private hidePublishingModal(): void {
    this.showPublishingModal = false;
    this.isSubmitting = false;
    document.body.style.overflow = 'auto';
  }

  private processVehicleData(formData: any): any {
    const vehicleField = this.formFields.find(
      (f) => f.type === 'vehicle-selector'
    );
    if (vehicleField && formData[vehicleField.name]) {
      const vehicleData = formData[vehicleField.name];

      if (
        typeof vehicleData === 'object' &&
        vehicleData.brand &&
        vehicleData.model
      ) {
        formData.brand = vehicleData.brand;
        formData.model = vehicleData.model;
        delete formData[vehicleField.name];
      }
    }

    return formData;
  }

  private async prepareFormData(effectivePrice: number): Promise<any> {
    await this.updateProgress(0, 'Validating data...');

    let formData = { ...this.listingForm.value };
    formData = this.processVehicleData(formData);

    if (this.selectedCategory!.slug !== 'vehicles') {
      delete formData.features;
    }

    if (this.uploadedImageUrls.length > 0) {
      formData.images = this.uploadedImageUrls;
    }

    if (this.selectedLocationName) {
      const locationField = this.listingForm.get('location');
      const cityField = this.listingForm.get('city');

      if (locationField) {
        formData.location = this.selectedLocationName;
      } else if (cityField) {
        formData.city = this.selectedLocationName;
      }
    }

    const expirationDate = this.calculateExpirationDate();
    if (expirationDate) {
      formData.expires_at = expirationDate.toISOString();
    }

    formData.plan_name = this.selectedPlan!.name;
    formData.plan_price = effectivePrice;
    formData.plan_duration = this.selectedPlan!.features.listingDuration;

    if (
      this.selectedCategory!.slug === 'vehicles' &&
      this.selectedFeatures.length > 0
    ) {
      formData.features = this.selectedFeatures;
    }

    formData.use_free_relist = this.usingFreeRelist;

    const preservedFields = {
      plan_name: formData.plan_name,
      plan_price: formData.plan_price,
      plan_duration: formData.plan_duration,
      images: formData.images,
      expires_at: formData.expires_at,
      features: formData.features,
      use_free_relist: formData.use_free_relist,
    };

    Object.keys(formData).forEach((key) => {
      if (preservedFields.hasOwnProperty(key)) {
        return;
      }

      const control = this.listingForm.get(key);
      if (
        control?.disabled ||
        formData[key] === '' ||
        formData[key] === null ||
        formData[key] === undefined
      ) {
        delete formData[key];
      }
    });

    Object.keys(preservedFields).forEach((key) => {
      const value = preservedFields[key as keyof typeof preservedFields];
      if (value !== undefined && value !== null) {
        formData[key] = value;
      }
    });

    if (this.selectedCategory!.slug !== 'vehicles' && 'features' in formData) {
      delete formData.features;
    }

    await this.updateProgress(10, 'Data validation complete');
    return formData;
  }

  private async createListingWithProgress(formData: any): Promise<void> {
    await this.updateProgress(30, 'Processing images...');

    if (this.uploadedImageUrls.length > 0) {
      const imageProcessingTime = Math.min(
        this.uploadedImageUrls.length * 100,
        1000
      );
      await this.simulateProgress(
        30,
        50,
        imageProcessingTime,
        'Processing images...'
      );
    }

    await this.updateProgress(70, 'Submitting listing...');

    this.listingsService
      .createListing(
        this.selectedCategory!.slug,
        this.selectedSubcategory!.slug,
        formData,
        this.usingFreeRelist
      )
      .subscribe({
        next: async (response) => {
          await this.updateProgress(90, 'Sending for review...');

          if (this.currentUser) {
            this.authService.forceRefreshUser().subscribe();
          }

          if (this.usingFreeRelist) {
            this.checkFreeRelistCredits();
          }

          await this.updateProgress(100, 'Submitted for Review!');

          setTimeout(() => {
            this.hidePublishingModal();
            this.showPendingApprovalModal = true;
          }, 500);
        },
        error: (error) => {
          this.hidePublishingModal();
          throw error;
        },
      });
  }

  private async updateProgress(progress: number, stage: string): Promise<void> {
    this.publishingProgress = progress;
    this.publishingStage = stage;

    this.currentStageIndex = this.publishingStages.findIndex(
      (s) => s.progress >= progress
    );
    if (this.currentStageIndex === -1) {
      this.currentStageIndex = this.publishingStages.length - 1;
    }

    await new Promise((resolve) => setTimeout(resolve, 200));
  }

  private async simulateProgress(
    start: number,
    end: number,
    duration: number,
    stage: string
  ): Promise<void> {
    const steps = 10;
    const stepDuration = duration / steps;
    const progressStep = (end - start) / steps;

    for (let i = 0; i <= steps; i++) {
      const currentProgress = start + progressStep * i;
      await this.updateProgress(currentProgress, stage);
      if (i < steps) {
        await new Promise((resolve) => setTimeout(resolve, stepDuration));
      }
    }
  }

  closeSuccessModal(): void {
    this.showSuccessModal = false;
    this.imageUploadService.clearSession();
    this.router.navigate(['/my-listings']);
  }

  closePendingApprovalModal(): void {
    this.showPendingApprovalModal = false;
    this.imageUploadService.clearSession();
    this.router.navigate(['/my-listings']);
  }

  clearMessages(): void {
    this.successMessage = '';
    this.errorMessage = '';
    this.descriptionGenerationError = '';
  }

  resetForm(): void {
    this.currentStep = 1;
    this.selectedCategory = null;
    this.selectedSubcategory = null;
    this.selectedPlan = this.plans[0];
    this.subcategories = [];
    this.formFields = [];
    this.vehicleFeatures = {};
    this.selectedFeatures = [];
    this.listingForm = this.fb.group({});
    this.uploadedImageUrls = [];
    this.imageUploadProgress = { uploaded: 0, total: 0 };
    this.imageUploadErrors = [];
    this.showSuccessModal = false;
    this.showPendingApprovalModal = false;
    this.showPlanUpgradeSuggestion = false;
    this.suggestedPlan = null;
    this.clearMessages();
    this.imageUploadService.generateNewSession();
    this.isGeneratingDescription = false;
    this.generatedDescriptionMetadata = null;
    this.selectedLocationName = '';
  }

  getPlanRecommendation(): PricingPlan | null {
    const description = this.listingForm.get('description')?.value || '';
    const imageCount = this.uploadedImageUrls.length;

    for (const plan of this.plans) {
      if (
        imageCount <= plan.features.imagesAllowed &&
        description.length <= plan.features.descriptionLength
      ) {
        return plan;
      }
    }

    return null;
  }

  getContentSummary(): {
    images: number;
    characters: number;
    hasContent: boolean;
  } {
    const description = this.listingForm.get('description')?.value || '';
    const imageCount = this.getImageCount();

    return {
      images: imageCount,
      characters: description.length,
      hasContent: imageCount > 0 || description.length > 0,
    };
  }
}
